import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import widgetRegistry from './widgetRegistry';
import { THREE_D_IDS } from './extraVariants';
import VARIANT_LISTS, { getVariantMeta } from './variantFamilies';
import { buildStylePreviewConfig } from './widgetCatalog';
import WidgetMiniPreview, { useInView } from './WidgetMiniPreview';
import {
    CUSTOM_STYLE_TILE, CUSTOMIZABLE_CATEGORIES,
    AppearanceFields, TitleFields, GaugeShapeFields, TableShapeFields, MetricShapeFields, MetricFieldsEditor,
} from './WidgetStyleEditor';

const MAX_GRID_COLS = 12;

const FIELD_LABELS = {
    value: 'Value',
    max: 'Max',
    precision: 'Decimal places',
    unit: 'Unit',
    label: 'Label',
    text: 'Note text',
    currentLabel: 'Current label',
    previousLabel: 'Previous label',
    compareFieldTitle: 'Compare field (Y-axis)',
};

const CHART_FIELDS_BY_VARIANT = {
    scatter: ['compareFieldTitle'],
    bubble: ['compareFieldTitle'],
};

const EXTRA_FIELDS_BY_VARIANT = {
    sparkline: ['unit'],
    comparison: ['currentLabel', 'previousLabel', 'unit'],
    clock: [],
    note: ['text'],
    'alert-feed': [],
    'kpi-grid': [],
    'energy-flow': ['max', 'unit'],
    weather: [],
    'maintenance-tickets': [],
    leaderboard: ['unit'],
    'health-score': [],
};

const STYLE_KEYS = [
    'color', 'background', 'shadow', 'borderStyle', 'borderWidth', 'borderRadius', 'fontSize', 'fontWeight', 'textColor',
    'titleColor', 'titleBg', 'titleFontSize', 'contentWidth', 'contentHeight',
    'customTrackShape', 'customFillStyle', 'customValueDisplay', 'customOrientation', 'customThickness',
    'customShowIcon', 'customIconClass', 'customShowTrend',
    'customStriped', 'customBordered', 'customCompact', 'customDarkHeader', 'customHover',
];
const NUMERIC_STYLE_KEYS = new Set(['borderWidth', 'borderRadius', 'fontSize', 'titleFontSize', 'contentWidth', 'contentHeight', 'customThickness']);

function fieldsForWidget(type, variant) {
    const meta = widgetRegistry[type];
    if (!meta) return [];
    const allKeys = Object.keys(meta.defaultSettings || {}).filter((k) => k !== 'color');
    if (meta.variantFamily === 'chart') return [...allKeys, ...(CHART_FIELDS_BY_VARIANT[variant] || [])];
    if (meta.variantFamily !== 'extra') return allKeys;
    if (THREE_D_IDS.includes(variant)) return ['max', 'unit'];
    return EXTRA_FIELDS_BY_VARIANT[variant] || allKeys;
}

function inputTypeFor(key) {
    if (key === 'value' || key === 'max' || key === 'precision') return 'number';
    if (key === 'text') return 'textarea';
    return 'text';
}

const StyleTile = ({ previewBaseConfig, style, selected, onSelect }) => {
    const [previewRef, inView] = useInView('300px 0px', '.widget-settings-drawer__body');
    const previewConfig = useMemo(
        () => buildStylePreviewConfig(previewBaseConfig, style.id),
        [previewBaseConfig, style.id],
    );

    return (
        <div
            role="button"
            tabIndex={0}
            className={`widget-tile widget-settings-drawer__style-tile${selected ? ' widget-tile--selected' : ''}`}
            title={style.label}
            onClick={onSelect}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect();
                }
            }}
        >
            <div className="widget-tile__preview" ref={previewRef}>
                {inView && <WidgetMiniPreview config={previewConfig} />}
            </div>
            <div className="widget-tile__label">{style.label}</div>
        </div>
    );
};

const WidgetSettingsPanel = ({ config, onSave, onClose }) => {
    const meta = widgetRegistry[config.type];
    const [title, setTitle] = useState(config.title || '');
    const [values, setValues] = useState({ color: '#4318FF', shadow: 'sm', ...config.settings });
    const [variant, setVariant] = useState(config.variant || meta.defaultVariant);
    const [widthUnits, setWidthUnits] = useState(config.layout?.w ?? meta.defaultLayout.w);
    const [heightUnits, setHeightUnits] = useState(config.layout?.h ?? meta.defaultLayout.h);
    const minW = config.layout?.minW ?? meta.defaultLayout.minW ?? 1;
    const minH = config.layout?.minH ?? meta.defaultLayout.minH ?? 1;

    const isCustomStyle = variant === 'custom';
    const rawStyleOptions = meta.variantFamily ? (VARIANT_LISTS[meta.variantFamily] || []) : [];
    const styleOptions = CUSTOMIZABLE_CATEGORIES.has(meta.variantFamily) ? [...rawStyleOptions, CUSTOM_STYLE_TILE] : rawStyleOptions;
    const isMetricCustom = isCustomStyle && meta.variantFamily === 'metric';
    const fields = fieldsForWidget(config.type, variant).filter((key) => (
        key !== 'value' && !(isMetricCustom && key === 'unit')
    ));

    const setField = (key, val) => setValues((prev) => ({ ...prev, [key]: val }));
    const patchValues = (patch) => setValues((prev) => ({ ...prev, ...patch }));
    const selectVariant = (nextVariant) => {
        setVariant(nextVariant);
        const variantMeta = getVariantMeta(meta.variantFamily, nextVariant);
        const layoutSpec = variantMeta?.layout || meta.defaultLayout;
        setWidthUnits(layoutSpec.w);
        setHeightUnits(layoutSpec.h);
    };

    const previewBaseConfig = useMemo(
        () => ({ ...config, title, variant, settings: { ...config.settings, ...values } }),
        [config, title, variant, values],
    );

    const handleSave = () => {
        const settings = { ...config.settings };
        STYLE_KEYS.forEach((key) => {
            if (values[key] === undefined) return;
            settings[key] = NUMERIC_STYLE_KEYS.has(key) ? (Number(values[key]) || 0) : values[key];
        });

        if (isMetricCustom) {
            settings.customFields = (values.customFields || [])
                .filter((f) => (f.title || '').trim())
                .map((f) => ({
                    title: (f.title || '').trim(),
                    unit: (f.unit || '').trim(),
                    position: f.position,
                }));
        }

        fields.forEach((key) => {
            const type = inputTypeFor(key);
            settings[key] = type === 'number' ? Number(values[key]) : values[key];
        });

        onSave({
            title,
            variant,
            settings,
            layout: {
                w: Math.max(minW, Math.min(MAX_GRID_COLS, Number(widthUnits) || minW)),
                h: Math.max(minH, Number(heightUnits) || minH),
            },
        });
        onClose();
    };

    return createPortal(
        <aside className="widget-settings-drawer">
            <div className="widget-settings-drawer__header">
                <i className="fas fa-sliders-h"></i>
                <span>Widget Settings</span>
                <button type="button" className="widget-settings-drawer__close" title="Close" onClick={onClose}>
                    <i className="fas fa-times"></i>
                </button>
            </div>

            <div className="widget-settings-drawer__body">
                <label className="widget-settings-drawer__field">
                    <span>Title</span>
                    <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
                </label>

                <TitleFields settings={values} onChange={patchValues} />

                {styleOptions.length > 0 && (
                    <div className="widget-settings-drawer__field">
                        <span>Style ({styleOptions.length})</span>
                        <div className="widget-settings-drawer__style-grid">
                            {styleOptions.map((style) => (
                                <StyleTile
                                    key={style.id}
                                    previewBaseConfig={previewBaseConfig}
                                    style={style}
                                    selected={variant === style.id}
                                    onSelect={() => selectVariant(style.id)}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {isCustomStyle && meta.variantFamily === 'gauge' && (
                    <GaugeShapeFields settings={values} onChange={patchValues} />
                )}
                {isCustomStyle && meta.variantFamily === 'metric' && (
                    <MetricShapeFields settings={values} onChange={patchValues} />
                )}
                {isCustomStyle && meta.variantFamily === 'table' && (
                    <TableShapeFields settings={values} onChange={patchValues} />
                )}

                <AppearanceFields settings={values} onChange={patchValues} />

                <div className="widget-settings-drawer__row">
                    <label className="widget-settings-drawer__field">
                        <span>Width (cols)</span>
                        <input
                            type="number"
                            min={minW}
                            max={MAX_GRID_COLS}
                            step="0.1"
                            value={widthUnits}
                            onChange={(e) => setWidthUnits(e.target.value)}
                        />
                    </label>
                    <label className="widget-settings-drawer__field">
                        <span>Height (rows)</span>
                        <input
                            type="number"
                            min={minH}
                            step="0.1"
                            value={heightUnits}
                            onChange={(e) => setHeightUnits(e.target.value)}
                        />
                    </label>
                </div>

                {isMetricCustom && (
                    <MetricFieldsEditor
                        fields={values.customFields}
                        onChange={(nextFields) => setField('customFields', nextFields)}
                    />
                )}

                {fields.map((key) => {
                    const type = inputTypeFor(key);
                    const label = FIELD_LABELS[key] || key;
                    const value = values[key] ?? '';
                    if (type === 'textarea') {
                        return (
                            <label className="widget-settings-drawer__field" key={key}>
                                <span>{label}</span>
                                <textarea value={value} onChange={(e) => setField(key, e.target.value)} rows={3} />
                            </label>
                        );
                    }
                    return (
                        <label className="widget-settings-drawer__field" key={key}>
                            <span>{label}</span>
                            <input
                                type={type}
                                value={value}
                                onChange={(e) => setField(key, e.target.value)}
                            />
                        </label>
                    );
                })}
            </div>

            <div className="widget-settings-drawer__footer">
                <button type="button" className="widget-settings-drawer__cancel" onClick={onClose}>Cancel</button>
                <button type="button" className="widget-settings-drawer__save" onClick={handleSave}>Save</button>
            </div>
        </aside>,
        document.body,
    );
};

export default WidgetSettingsPanel;
